import React, { useRef, useState, useEffect } from "react";
import { cn, sanitizeFileName } from "@/lib/utils";
import { Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";

interface InvestmentViewProps {
  className?: string;
  leftImageUrl?: string;
  rightImageUrl?: string;
  isEditMode?: boolean;
  onLeftImageChange?: (url: string | null) => void;
  onRightImageChange?: (url: string | null) => void;
}

export function InvestmentView({
  className,
  leftImageUrl,
  rightImageUrl,
  isEditMode = false,
  onLeftImageChange,
  onRightImageChange,
}: InvestmentViewProps) {
  const leftFileInputRef = useRef<HTMLInputElement>(null);
  const rightFileInputRef = useRef<HTMLInputElement>(null);
  
  const [localLeftImage, setLocalLeftImage] = useState<string | null>(leftImageUrl || null);
  const [localRightImage, setLocalRightImage] = useState<string | null>(rightImageUrl || null);
  const [leftImageScale, setLeftImageScale] = useState(100);
  const [rightImageScale, setRightImageScale] = useState(100);
  const [isUploadingLeft, setIsUploadingLeft] = useState(false);
  const [isUploadingRight, setIsUploadingRight] = useState(false);

  useEffect(() => {
    setLocalLeftImage(leftImageUrl || null);
  }, [leftImageUrl]);

  useEffect(() => {
    setLocalRightImage(rightImageUrl || null);
  }, [rightImageUrl]);

  const handleFileUpload = async (file: File, side: 'left' | 'right') => {
    const setUploading = side === 'left' ? setIsUploadingLeft : setIsUploadingRight;
    const setLocalImage = side === 'left' ? setLocalLeftImage : setLocalRightImage;
    const onImageChange = side === 'left' ? onLeftImageChange : onRightImageChange;
    
    setUploading(true);
    try {
      const safeName = sanitizeFileName(file.name);
      const filePath = `investment-illustrations/${side}-${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("beratung-documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("beratung-documents")
        .getPublicUrl(filePath);

      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error("Konnte keine öffentliche URL erzeugen");

      setLocalImage(publicUrl);
      onImageChange?.(publicUrl);
    } catch (err) {
      console.error(`${side} image upload failed:`, err);
    } finally {
      setUploading(false);
    }
  };

  const handleLeftFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, 'left');
  };

  const handleRightFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file, 'right');
  };

  const handleRemoveImage = (side: 'left' | 'right') => {
    if (side === 'left') {
      setLocalLeftImage(null);
      onLeftImageChange?.(null);
      if (leftFileInputRef.current) leftFileInputRef.current.value = '';
    } else {
      setLocalRightImage(null);
      onRightImageChange?.(null);
      if (rightFileInputRef.current) rightFileInputRef.current.value = '';
    }
  };

  const renderImagePanel = (
    side: 'left' | 'right',
    imageUrl: string | null,
    isUploading: boolean,
    imageScale: number,
    setImageScale: (val: number) => void,
    fileInputRef: React.RefObject<HTMLInputElement>,
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onRemove: () => void
  ) => (
    <div className="border-2 border-primary rounded-lg p-4 h-full flex flex-col items-center justify-center min-h-[350px]">
      {imageUrl ? (
        <div className="relative w-full h-full flex flex-col items-center justify-center gap-2">
          <img
            src={imageUrl}
            alt={side === 'left' ? 'Investment Chart' : 'Investment Visualization'}
            className="object-contain transition-transform"
            style={{
              maxWidth: `${imageScale}%`,
              maxHeight: `${Math.min(320, 320 * imageScale / 100)}px`
            }}
          />
          {isEditMode && (
            <>
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-0 right-0 h-8 w-8"
                onClick={onRemove}
              >
                <X className="h-4 w-4" />
              </Button>
              <div className="w-full max-w-[180px] mt-2">
                <Label className="text-xs text-muted-foreground mb-1 block">
                  Größe: {imageScale}%
                </Label>
                <Slider
                  value={[imageScale]}
                  onValueChange={([v]) => setImageScale(v)}
                  min={50}
                  max={300}
                  step={10}
                  className="w-full"
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center p-4 space-y-4 w-full h-full">
          {isEditMode ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onFileChange}
                className="hidden"
              />
              <Button
                variant="outline"
                disabled={isUploading}
                className="border-dashed border-2 h-32 w-full flex flex-col gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {isUploading ? "Upload läuft..." : "Grafik hochladen"}
                </span>
              </Button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Upload className="h-8 w-8" />
              <span className="text-sm">Bild wird hier angezeigt</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={cn("p-6 space-y-4", className)}>
      {/* Title section */}
      <div className="space-y-1">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Dinge die wir </span>
          <span className="text-primary italic">täglich nutzen!</span>
        </h2>
        <p className="text-lg italic text-muted-foreground">
          Heute investieren, morgen profitieren.
        </p>
      </div>

      {/* Subtitle */}
      <p className="text-base">
        Bsp: 200 Euro monatlich, 25 Jahre lang sparen - Bei welchem Kursverlauf fühlen Sie sich am wohlsten?
      </p>

      {/* 12-column grid layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left side: Chart (7 columns) */}
        <div className="col-span-12 lg:col-span-7">
          {renderImagePanel(
            'left',
            localLeftImage,
            isUploadingLeft,
            leftImageScale,
            setLeftImageScale,
            leftFileInputRef,
            handleLeftFileChange,
            () => handleRemoveImage('left')
          )}
        </div>

        {/* Right side: Products (5 columns) */}
        <div className="col-span-12 lg:col-span-5">
          {renderImagePanel(
            'right',
            localRightImage,
            isUploadingRight,
            rightImageScale,
            setRightImageScale,
            rightFileInputRef,
            handleRightFileChange,
            () => handleRemoveImage('right')
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-muted-foreground mt-4">
        Die Kursentwicklung basiert auf dem FTSE World Government Bond – Developed Markets Index (EUR) sowie dem MSCI ACWI IMI Index (EUR). 
        Der Chart ist linear skaliert. Hinweis: Vergangene Entwicklungen sind kein verlässlicher Indikator für zukünftige Ergebnisse.
      </p>
    </div>
  );
}
